import React, { useRef, useCallback, useEffect } from 'react';

interface HtmlEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    className?: string;
    isDark?: boolean;
    rows?: number;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({ value, onChange, placeholder = '', className = '', isDark = true, rows = 4 }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const isInternalChange = useRef(false);

    useEffect(() => {
        if (editorRef.current && !isInternalChange.current) {
            if (editorRef.current.innerHTML !== value) {
                editorRef.current.innerHTML = value || '';
            }
        }
        isInternalChange.current = false;
    }, [value]);

    const handleInput = useCallback(() => {
        if (editorRef.current) {
            isInternalChange.current = true;
            onChange(editorRef.current.innerHTML);
        }
    }, [onChange]);

    const exec = useCallback((command: string, val?: string) => {
        document.execCommand(command, false, val);
        editorRef.current?.focus();
        handleInput();
    }, [handleInput]);

    const insertLink = useCallback(() => {
        const url = prompt('Enter URL:');
        if (url) exec('createLink', url);
    }, [exec]);

    const btnClass = `px-2 py-1 rounded text-xs font-bold transition-colors ${isDark ? 'hover:bg-slate-600 text-slate-300' : 'hover:bg-slate-200 text-slate-600'}`;
    const activeBtnClass = `${btnClass} ${isDark ? 'bg-slate-600' : 'bg-slate-200'}`;
    const minH = rows * 24;

    return (
        <div className={`rounded-lg border ${isDark ? 'border-slate-600 bg-slate-800' : 'border-slate-300 bg-white'} ${className}`}>
            {/* Toolbar */}
            <div className={`flex flex-wrap gap-0.5 px-2 py-1 border-b ${isDark ? 'border-slate-600 bg-slate-700/50' : 'border-slate-200 bg-slate-50'}`}>
                <button type="button" onClick={() => exec('bold')} className={btnClass} title="Bold"><b>B</b></button>
                <button type="button" onClick={() => exec('italic')} className={btnClass} title="Italic"><i>I</i></button>
                <button type="button" onClick={() => exec('underline')} className={btnClass} title="Underline"><u>U</u></button>
                <span className={`mx-1 ${isDark ? 'border-l border-slate-600' : 'border-l border-slate-300'}`} />
                <button type="button" onClick={() => exec('formatBlock', '<h2>')} className={btnClass} title="Heading 2">H2</button>
                <button type="button" onClick={() => exec('formatBlock', '<h3>')} className={btnClass} title="Heading 3">H3</button>
                <button type="button" onClick={() => exec('formatBlock', '<p>')} className={btnClass} title="Paragraph">P</button>
                <span className={`mx-1 ${isDark ? 'border-l border-slate-600' : 'border-l border-slate-300'}`} />
                <button type="button" onClick={() => exec('insertUnorderedList')} className={btnClass} title="Bullet List">• List</button>
                <button type="button" onClick={() => exec('insertOrderedList')} className={btnClass} title="Numbered List">1. List</button>
                <span className={`mx-1 ${isDark ? 'border-l border-slate-600' : 'border-l border-slate-300'}`} />
                <button type="button" onClick={insertLink} className={btnClass} title="Insert Link">🔗</button>
                <button type="button" onClick={() => exec('removeFormat')} className={btnClass} title="Clear Formatting">✕</button>
            </div>
            {/* Editor Area */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onBlur={handleInput}
                data-placeholder={placeholder}
                className={`px-3 py-2 outline-none prose prose-sm max-w-none overflow-y-auto ${isDark ? 'text-white prose-invert' : 'text-slate-900'}`}
                style={{ minHeight: `${minH}px`, maxHeight: '300px' }}
                suppressContentEditableWarning
            />
        </div>
    );
};

export default HtmlEditor;
